import z from 'zod';
import { PaymentMethod } from '@/features/dashboard/tourConfig/types/PaymentMethod.ts';
import { Currency } from '@/features/dashboard/paymentConfig/types/Currency.ts';
import Option from '@/components/form/types/Option.ts';

export enum PaymentTiming {
  AT_RESERVATION = 'AT_RESERVATION',
  AFTER_RESERVATION = 'AFTER_RESERVATION',
}

export const paymentTimingLabels: Record<PaymentTiming, string> = {
  [PaymentTiming.AT_RESERVATION]: 'Płatność przy rezerwacji',
  [PaymentTiming.AFTER_RESERVATION]: 'Płatność do N dni po rezerwacji',
};

export const getPaymentTimingOptions = (): Option[] => [
  {
    value: PaymentTiming.AT_RESERVATION,
    label: paymentTimingLabels[PaymentTiming.AT_RESERVATION],
  },
  {
    value: PaymentTiming.AFTER_RESERVATION,
    label: paymentTimingLabels[PaymentTiming.AFTER_RESERVATION],
  },
];

const bankAccountSelectionSchema = z.object({
  currency: z.enum(Currency),
  bankAccountId: z.string().min(1, 'Wybierz konto bankowe'),
});

const baseInstallmentsSchema = z.object({
  paymentMethods: z
    .array(z.enum(PaymentMethod))
    .min(1, 'Musisz wybrać przynajmniej jedną metodę płatności'),
  paymentTiming: z.enum(PaymentTiming).optional(),
  dueDaysBeforeStart: z.any().optional(),
  dueDaysAfterReservation: z.any().optional(),
  basePricePercentage: z.any().optional(),
});

export const installmentsSchema = baseInstallmentsSchema.extend({
  basePricePercentage: z.coerce
    .number({
      error: 'Procent ceny musi być liczbą',
    })
    .min(1, 'Procent ceny musi być większy lub równy 1')
    .max(100, 'Procent ceny musi być mniejszy lub równy 100'),
});

const numericDaysFieldSchema = z.coerce
  .number({
    error: 'Liczba dni musi być liczbą',
  })
  .int({ error: 'Liczba dni musi być liczbą całkowitą' })
  .nonnegative('Liczba dni musi być większa lub równa 0');

export const tourInstallmentsSchema = z
  .discriminatedUnion('allowInstallments', [
    z.object({
      allowInstallments: z.literal(false),
      installments: z
        .array(baseInstallmentsSchema)
        .length(
          1,
          'Gdy płatność na raty jest wyłączona, musi być dokładnie jedna rata',
        ),
      bankAccountSelections: z
        .array(bankAccountSelectionSchema)
        .optional()
        .default([]),
    }),
    z.object({
      allowInstallments: z.literal(true),
      installments: z
        .array(installmentsSchema)
        .min(2, 'Musisz dodać przynajmniej dwie raty')
        .superRefine((items, ctx) => {
          items.forEach((item, idx) => {
            if (idx === 0) {
              if (!item.paymentTiming) {
                ctx.addIssue({
                  code: 'custom',
                  path: [idx, 'paymentTiming'],
                  message: 'Wybierz moment płatności',
                });
                return;
              }
              if (item.paymentTiming === PaymentTiming.AFTER_RESERVATION) {
                const parsed = numericDaysFieldSchema.safeParse(
                  item.dueDaysAfterReservation,
                );
                if (!parsed.success) {
                  ctx.addIssue({
                    code: 'custom',
                    path: [idx, 'dueDaysAfterReservation'],
                    message: parsed.error.issues[0].message,
                  });
                }
              }
            } else {
              const parsed = numericDaysFieldSchema.safeParse(
                item.dueDaysBeforeStart,
              );
              if (!parsed.success) {
                ctx.addIssue({
                  code: 'custom',
                  path: [idx, 'dueDaysBeforeStart'],
                  message: parsed.error.issues[0].message,
                });
              }
            }
          });
        }),
      bankAccountSelections: z
        .array(bankAccountSelectionSchema)
        .optional()
        .default([]),
    }),
  ])
  .refine(
    (data) => {
      // check if installments.basePricePercentage sums to 100 when allowInstallments is true
      if (data.allowInstallments) {
        const totalPercentage = data.installments.reduce(
          (sum, installment) => sum + installment.basePricePercentage,
          0,
        );
        return totalPercentage === 100;
      }
      return true;
    },
    {
      message: 'Suma procentów wszystkich rat musi wynosić dokładnie 100%',
      path: ['installments'],
    },
  );

export type TourInstallmentInputForm = z.input<typeof tourInstallmentsSchema>;
export type TourInstallmentForm = z.infer<typeof tourInstallmentsSchema>;
export type BaseInstallmentSchemaType = z.infer<typeof baseInstallmentsSchema>;
